// 発送分割モーダル: 遅れるカードを選んで新しい発送IDへ切り出す

import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { useTranslation } from "react-i18next";

import './ShipDetailModal.css';
import { fetchShipLots, splitShipment } from '../../../../utils/apiClient';

const ShipSplitModal = ({ isOpen, onClose, shipInfo, cardList, onSplit }) => {
    const { t } = useTranslation();

    const [selectedIds, setSelectedIds] = useState([]);
    const [lotMode, setLotMode] = useState('hold');
    const [lotId, setLotId] = useState('');
    const [expectedShipDate, setExpectedShipDate] = useState('');
    const [lots, setLots] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setSelectedIds([]);
        setLotMode('hold');
        setLotId('');
        setExpectedShipDate('');

        // 移動先の候補は発送済み以外のロット。元のロットは除く。
        const loadLots = async () => {
            try {
                const response = await fetchShipLots({ per_page: 100 });
                if (!response.ok) return;
                const payload = await response.json();
                const currentLotId = shipInfo.lot?.lot_id;
                setLots((payload.data ?? []).filter(lot => lot.status !== 'shipped' && lot.lotId !== currentLotId));
            } catch (error) {
                console.error('ロット一覧の取得に失敗しました:', error);
            }
        };
        loadLots();
    }, [isOpen]);

    const toggleCard = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
    };

    const submit = async () => {
        if (selectedIds.length === 0) {
            alert(t('ship_split.need_cards'));
            return;
        }
        if (selectedIds.length >= cardList.length) {
            alert(t('ship_split.need_rest'));
            return;
        }
        if (lotMode === 'lot' && !lotId) {
            alert(t('ship_split.need_lot'));
            return;
        }
        if (!window.confirm(t('ship_split.confirm', { count: selectedIds.length }))) {
            return;
        }

        setSubmitting(true);
        try {
            const response = await splitShipment(shipInfo.id, {
                card_ids: selectedIds,
                lot_mode: lotMode,
                lot_id: lotMode === 'lot' ? lotId : null,
                expected_ship_date: expectedShipDate || null,
            });
            const data = await response.json();
            if (!response.ok) {
                const messages = data.errors ? Object.values(data.errors).flat().join('\n') : (data.message || t('common.error'));
                alert(messages);
                return;
            }
            alert(`${t('ship_split.done', { id: data.id })}\n${t('ship_split.sheet_notice')}`);
            onSplit(data);
        } catch (error) {
            console.error('発送分割に失敗しました:', error);
            alert(t('common.error'));
        } finally {
            setSubmitting(false);
        }
    };

    const lotLabel = (lot) => {
        const order = lot.locationsOrderNumber ? `${lot.locationsOrderNumber} / ` : '';
        const status = t(`lot.statuses.${lot.status}`, { defaultValue: lot.status });
        return `${order}${lot.lotId}（${status}・${lot.totalCardCount ?? 0}）`;
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className="ship-detail-modal"
            overlayClassName="ship-detail-modal-overlay"
            contentLabel="Ship Split Modal"
        >
            <div className="ship-detail-modal-title">{t('ship_split.title')}</div>
            <div className="clearfix"></div>
            <div className="ship-detail-modal-form">
                <p>{t('ship_split.description')}</p>

                <div className="ship-detail-modal-form-title">{t('ship_split.select_cards')}</div>
                <div className="ship-detail-modal-form-content">
                    {cardList.map(card => (
                        <label key={`split-${card.id}`} style={{ display: 'block', marginBottom: '0.4rem' }}>
                            <input
                                type="checkbox"
                                checked={selectedIds.includes(card.id)}
                                onChange={() => toggleCard(card.id)}
                            />
                            {' '}{card.package_name} / {card.name}{card.is_psa_enabled == 1 && `(PSA${card.psa})`}
                            {' '}{card.rank == 9 ? t('tr.num') : `${card.rank}${t('tr.rank')}`}
                        </label>
                    ))}
                </div>

                <div className="ship-detail-modal-form-title">{t('ship_split.lot')}</div>
                <div className="ship-detail-modal-form-content">
                    {['hold', 'lot', 'auto'].map(mode => (
                        <label key={mode} style={{ display: 'block', marginBottom: '0.4rem' }}>
                            <input type="radio" name="lot_mode" value={mode} checked={lotMode === mode} onChange={() => setLotMode(mode)} />
                            {' '}{t(`ship_split.mode_${mode}`)}
                        </label>
                    ))}
                    {lotMode === 'lot' &&
                        <select className="ship-detail-modal-form-content-select" value={lotId} onChange={e => setLotId(e.target.value)}>
                            <option value="">{t('ship_split.select_lot')}</option>
                            {lots.map(lot => <option key={lot.lotId} value={lot.lotId}>{lotLabel(lot)}</option>)}
                        </select>
                    }
                </div>

                <div className="ship-detail-modal-form-title">{t('ship_split.expected_ship_date')}</div>
                <div className="ship-detail-modal-form-content">
                    <input className="ship-detail-modal-form-content-input" type="date" value={expectedShipDate} onChange={e => setExpectedShipDate(e.target.value)} />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                    <button className="ship-detail-modal-form-button" onClick={onClose} disabled={submitting}>{t('ship_split.cancel')}</button>
                    <button className="ship-detail-modal-form-button" onClick={submit} disabled={submitting}>{t('ship_split.submit')}</button>
                </div>
            </div>
        </Modal>
    );
};

export default ShipSplitModal;
